"use client";

import { useEffect } from "react";

/**
 * Reloads the page once when a Server Action call hits a newer deployment.
 *
 * Action IDs change on every build. A tab opened before a deploy still holds
 * the old IDs, so its next cart add, widget load or form submit gets a 404
 * with `x-nextjs-action-not-found: 1` ("Failed to find Server Action" in the
 * server log, ~100 on 2026-09-27 alone) and simply fails. Next.js only
 * recovers from version skew on navigation, not on actions, and callers catch
 * the error in too many places to handle it at each one — so this watches the
 * response itself and reloads, which fetches the current build. The cart and
 * session live server-side, so they survive the reload.
 *
 * Guarded by a timestamp in sessionStorage so a server that keeps answering
 * "not found" cannot trap the tab in a reload loop.
 */
const RELOADED_AT = "sf-stale-deploy-reloaded-at";
const MIN_INTERVAL_MS = 60_000;

export function shouldReloadForStaleDeploy(res: Response): boolean {
  return (
    res.status === 404 && res.headers.get("x-nextjs-action-not-found") === "1"
  );
}

function reloadOnce() {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT) ?? 0);
    if (Date.now() - last < MIN_INTERVAL_MS) return;
    sessionStorage.setItem(RELOADED_AT, String(Date.now()));
  } catch {
    // No sessionStorage (private mode, blocked storage): reload anyway — a
    // stale tab is broken either way, and one reload is the cure.
  }
  window.location.reload();
}

export default function StaleDeployReload() {
  useEffect(() => {
    const original = window.fetch;
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const res = await original(...args);
      if (shouldReloadForStaleDeploy(res)) reloadOnce();
      return res;
    };
    return () => {
      window.fetch = original;
    };
  }, []);

  return null;
}
